import { z } from "zod";
import { ISSUE_EVENT_KINDS, ISSUE_STATUSES, type IssueStatus, type Priority } from "./enums.js";
import { answersSchema, type Answers } from "./questions.js";
import { coordSchema, elementCodeSchema } from "./structure.js";

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

/** Plek: verdieping + optioneel ruimte + optioneel pin (x en y samen). */
export const locationInputSchema = z
  .object({
    id: z.uuid().optional(),
    floorId: z.uuid(),
    roomId: z.uuid().nullish(),
    x: coordSchema.nullish(),
    y: coordSchema.nullish(),
  })
  .refine((l) => (l.x == null) === (l.y == null), { message: "Pin heeft x én y nodig" });
export type LocationInput = z.infer<typeof locationInputSchema>;

export const issueCreateSchema = z.object({
  /** Door de client gegenereerd (offline, idempotent). */
  id: z.uuid(),
  title: z.string().trim().min(1, "Vul een korte omschrijving in").max(120),
  description: optionalText(4000),
  answers: answersSchema,
  installationId: z.uuid().nullish(),
  /** NLBE-SfB-element (optioneel). */
  elementCode: elementCodeSchema.nullish(),
  locations: z.array(locationInputSchema).max(50).default([]),
  photos: z.array(z.uuid()).max(20).default([]),
});
export type IssueCreate = z.infer<typeof issueCreateSchema>;

export const issueUpdateSchema = z
  .object({
    title: z.string().trim().min(1).max(120),
    description: optionalText(4000),
    answers: answersSchema,
    status: z.enum(ISSUE_STATUSES),
    installationId: z.uuid().nullable(),
    elementCode: elementCodeSchema.nullable(),
  })
  .partial();

export const locationUpdateSchema = z
  .object({
    floorId: z.uuid(),
    roomId: z.uuid().nullable(),
    x: coordSchema.nullable(),
    y: coordSchema.nullable(),
  })
  .partial()
  .refine((l) => (l.x === undefined) === (l.y === undefined) && (l.x === null) === (l.y === null), {
    message: "Pin heeft x én y nodig",
  });

export const commentSchema = z.object({ body: z.string().trim().min(1).max(4000) });
export const photoAttachSchema = z.object({ fileId: z.uuid() });

// ---------- antwoorden van de API ----------
export interface LocationDto {
  id: string;
  floorId: string;
  roomId: string | null;
  x: number | null;
  y: number | null;
  isPrimary: boolean;
  sort: number;
}

export interface IssueSummary {
  id: string;
  code: string;
  /** Code van de gekoppelde taak (T-0001). */
  taskCode?: string | null;
  seq: number;
  title: string;
  description: string | null;
  status: IssueStatus;
  priority: Priority;
  answers: Answers;
  installationId: string | null;
  /** NLBE-SfB-element ("56.24"), of null. */
  elementCode: string | null;
  taskId: string | null;
  locations: LocationDto[];
  photoCount: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface IssueEventDto {
  id: string;
  kind: (typeof ISSUE_EVENT_KINDS)[number];
  body: string | null;
  user: { id: string; name: string } | null;
  createdAt: string;
}

export interface IssueDetail extends IssueSummary {
  createdBy: { id: string; name: string } | null;
  /** Foto's en video's; `mime` ontbreekt bij lokale (nog niet geüploade) items uit oudere wachtrijen. */
  photos: { fileId: string; mime?: string | null }[];
  events: IssueEventDto[];
}

/** Realtime-bericht (SSE); alleen type + id, de client haalt zelf de data op. */
export interface RealtimeEvent {
  type:
    | "issue.updated"
    | "issue.deleted"
    | "building.updated"
    | "task.updated"
    | "intake.updated"
    | "dossier.updated"
    | "visit.updated";
  buildingId: string;
  id: string;
}
